import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { faChartLine, faTable } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RootState } from 'store';
import type { SalesAttribution as SalesAttributionModel } from 'model/salesDashboard';
import { SalesAttributionChart, type ChartRow } from './SalesAttributionChart/SalesAttributionChart';
import { SalesAttributionTable } from './SalesAttributionTable/SalesAttributionTable';
import styles from './SalesAttribution.module.scss';

type ViewMode = 'chart' | 'table';

const buildChartData = (salesAttribution: SalesAttributionModel): ChartRow[] => {
    if (!salesAttribution.series.length) return [];

    const months = salesAttribution.series[0].data.map(d => d.month);

    return months.map(month => {
        const row: ChartRow = { period: moment(month, 'YYYY-MM').format('MMM YYYY') };
        salesAttribution.series.forEach(series => {
            const point = series.data.find(d => d.month === month);
            row[series.key] = point?.netSales ?? 0;
        });
        return row;
    });
};

export const SalesAttribution: React.FC = React.memo(() => {
    const salesAttribution = useSelector((state: RootState) => state.salesDashboard.salesAttribution);
    const loadingSalesAttribution = useSelector((state: RootState) => state.salesDashboard.loadingSalesAttribution);
    const [viewMode, setViewMode] = useState<ViewMode>('chart');

    const chartData = salesAttribution ? buildChartData(salesAttribution) : [];

    const seriesLabels: Record<string, string> = salesAttribution
        ? Object.fromEntries(salesAttribution.series.map(s => [s.key, s.label]))
        : {};

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.title}>Sales attribution</h3>
                <div className={styles.actions}>
                    <button
                        className={`${styles.actionButton} ${viewMode === 'table' ? styles.actionButtonActive : ''}`}
                        onClick={() => setViewMode('table')}
                        title="Table view"
                    >
                        <FontAwesomeIcon icon={faTable} />
                    </button>
                    <button
                        className={`${styles.actionButton} ${viewMode === 'chart' ? styles.actionButtonActive : ''}`}
                        onClick={() => setViewMode('chart')}
                        title="Chart view"
                    >
                        <FontAwesomeIcon icon={faChartLine} />
                    </button>
                </div>
            </div>

            {loadingSalesAttribution && <div className={styles.loading}>Loading...</div>}

            {!loadingSalesAttribution && chartData.length === 0 && (
                <div className={styles.empty}>No data available</div>
            )}

            {!loadingSalesAttribution && chartData.length > 0 && viewMode === 'chart' && (
                <SalesAttributionChart
                    salesAttribution={salesAttribution!}
                    chartData={chartData}
                    seriesLabels={seriesLabels}
                />
            )}

            {!loadingSalesAttribution && salesAttribution && viewMode === 'table' && (
                <SalesAttributionTable salesAttribution={salesAttribution} />
            )}
        </div>
    );
});

SalesAttribution.displayName = 'SalesAttribution';
